import React from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SearchField,
  Box,
  SimpleList,
  Paginator,
  Divider,
  CircularProgress,
  Button,
  Typography,
} from "@tiny/components";
import { DialogProps } from "@tiny/components/src/Dialog/Dialog.types";
import { useAudiencesListDialog } from "./AudiencesListDialog.hooks";
import AudienceRow from "./AudienceRow";
import TinyImporterSample from "../TinyImporterSample/TinyImporterSample";
import { useTinyHost } from "@tiny/host-contract";

export interface AudiencesListDialogProps extends DialogProps {
  onSuccess?: (selected?: any) => void;
  pageName?: string;
  mode?: "update" | "export" | "select";
  title?: string;
  subtitle?: string | React.ReactNode;
  segmentIds?: string[];
}

function AudiencesListDialog(props: AudiencesListDialogProps) {
  const { open, onClose, onSuccess } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
    >
      <AudiencesListDialogContent onSuccess={onSuccess} {...props} />
    </Dialog>
  );
}

function AudiencesListDialogContent(props: AudiencesListDialogProps) {
  const {
    onClose,
    onSuccess,
    subtitle,
    mode = "update",
    title = "Update existing source",
  } = props;

  const {
    content,
    handlePageChange,
    handleSearchChange,
    handleSizeChange,
    itemsPerPage,
    itemsPerPageOptions,
    page,
    reloadAudiences,
    search,
    isLoading,
    totalCount,
  } = useAudiencesListDialog(props);

  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const isSelectionMode = mode === "select";

  const { navigate } = useTinyHost();

  const handleToggleSelection = React.useCallback((item: any) => {
    setSelectedItems((prev) => {
      const isAlreadySelected = prev.some(
        (selected) => selected.id === item.id
      );
      if (isAlreadySelected) {
        return prev.filter((selected) => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  }, []);

  const handleConfirmSelection = React.useCallback(() => {
    onSuccess?.(selectedItems);
    onClose();
  }, [selectedItems, onSuccess, onClose]);

  const handleNavigateToAudiences = React.useCallback(() => {
    navigate.to("/main/audience");
  }, []);

  const defaultSubtitle = React.useMemo(() => {
    if (isSelectionMode) {
      return <Box>Select audiences to import</Box>;
    }
    return (
      <>
        <Box>Please select the source to append the data</Box>
        <Box>
          <TinyImporterSample />
        </Box>
      </>
    );
  }, [isSelectionMode]);

  const noData = !isLoading && (!content || content.length === 0);

  return (
    <>
      <DialogTitle subtitle={subtitle || defaultSubtitle}>{title}</DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box
            width="100%"
            height="300px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgcolor="white"
          >
            <CircularProgress />
          </Box>
        ) : noData ? (
          <Box
            width="100%"
            height="300px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
          >
            <Typography
              variant="h4"
              color="grey"
              tone={800}
              mb={2}
              fontWeight="semiBold"
            >
              No audiences found
            </Typography>
            <Typography variant="body2" color="grey" tone={600} mb={6}>
              You don’t have any audiences yet. Create or import new ones to get
              started.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNavigateToAudiences}
            >
              Go to Audiences
            </Button>
          </Box>
        ) : (
          <>
            {isSelectionMode && selectedItems.length > 0 && (
              <Box mb={1}>
                <Typography variant="body2" color="grey" tone={700}>
                  {selectedItems.length} selected
                </Typography>
              </Box>
            )}
            <Box mb={3} display="flex" alignItems="center">
              <SearchField
                value={search}
                placeholder="Search"
                onChangeValue={handleSearchChange}
                style={{ flex: 1 }}
              />
            </Box>
            <SimpleList>
              {content?.map((row) => (
                <AudienceRow
                  key={row.id}
                  item={row}
                  reloadAudiences={reloadAudiences}
                  closeDialog={onClose}
                  onSuccess={onSuccess}
                  mode={mode}
                  segmentIds={props.segmentIds}
                  selectMode={isSelectionMode}
                  selected={selectedItems.some((item) => item.id === row.id)}
                  onClick={() => handleToggleSelection(row)}
                />
              ))}
            </SimpleList>
            <Divider mb={2} />
            <Paginator
              mt={4}
              totalCount={totalCount}
              page={page}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={itemsPerPageOptions}
              onItemsPerPageChange={handleSizeChange}
              onPageChange={handlePageChange}
              itemsPerPageLabel="results"
            />
          </>
        )}
      </DialogContent>

      {isSelectionMode && !noData && (
        <DialogActions>
          <Button onClick={onClose} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            color="primary"
            disabled={selectedItems.length === 0}
          >
            Confirm ({selectedItems.length})
          </Button>
        </DialogActions>
      )}
    </>
  );
}

export default AudiencesListDialog;
